/**
 * Compact, readable form of a stored `staff.classification_code`, for surfaces
 * that repeat it once per row.
 *
 * WHY. Codes are stored as `<award>:<stream>:<level>[:<paypoint>]` —
 * `MA000100:HOME_CARE_AGED:HCA3`, `MA000100:SACS:L4:1`,
 * `MA000018:DIRECT_CARE:DC3`. The roster board printed the whole path under
 * every worker's name, so a column of forty workers repeated the same
 * eighteen-character award prefix forty times and truncated mid-token. The award
 * is a property of the tenant's agreement, not of the individual, and is
 * identical for every worker on the same award — so on a per-row label it is
 * the one part carrying no information.
 *
 * This drops the award prefix and keeps stream, level and pay point:
 * `SACS L4.1`, `Home care HCA3`, `Direct care DC3`.
 *
 * NOT a pay label. `getStaffClassificationLabel` remains the right thing for pay
 * surfaces, where the award must be stated explicitly. This is for dense
 * operational views where the row is already scoped to one tenant.
 */

/** Streams seen in live data, plus the obvious siblings. */
const STREAM_LABELS: Record<string, string> = {
  SACS: 'SACS',
  HOME_CARE_AGED: 'Home care',
  HOME_CARE: 'Home care',
  DIRECT_CARE: 'Direct care',
  RESIDENTIAL: 'Residential',
  NURSING: 'Nursing',
  SUPPORT_SERVICES: 'Support services',
  ADMIN: 'Admin',
};

function titleCase(token: string): string {
  const cleaned = token.replace(/_+/g, ' ').trim().toLowerCase();
  if (!cleaned) return '';
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

/**
 * Shorten a classification code for a dense row label.
 *
 * Returns the input unchanged when it does not parse. Losing a classification
 * entirely would be worse than showing a long one: a coordinator uses it to
 * sanity-check that the person they are about to roster is graded for the work.
 */
export function shortClassificationGrade(code: string | null | undefined): string {
  if (code == null) return '';
  const raw = String(code).trim();
  if (!raw) return '';

  const parts = raw.split(':').filter((part) => part.trim().length > 0);
  // Anything that is not award:stream:level(:paypoint) is returned as-is rather
  // than guessed at.
  if (parts.length < 3) return raw;

  const [, stream, level, payPoint] = parts;
  if (!stream || !level) return raw;

  const streamLabel = STREAM_LABELS[stream.toUpperCase()] ?? titleCase(stream);
  const levelLabel = level.toUpperCase();

  // Pay point is kept because it moves the rate, but only when it is a plain
  // number — anything else is unrecognised and dropping it would be a guess.
  const suffix = payPoint && /^\d+$/.test(payPoint) ? `.${payPoint}` : '';

  const label = `${streamLabel} ${levelLabel}${suffix}`.trim();
  return label || raw;
}
